/**
 * App-wide UI tokens.
 *
 * Shared by every page so the redesign stays consistent as it rolls out. The
 * status palette is fixed and reserved: these four colours mean good / warning /
 * serious / critical and are never reused to distinguish categories, so a colour
 * never has to be guessed at. Each is always paired with a label — colour alone
 * never carries the meaning.
 */
export const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
};

/** Neutral surface + accent used by bars and progress tracks. */
export const SEQUENTIAL = {
  track: '#eef2f7',
  base: '#2a78d6',
};

export const DOC_STATUS = {
  completed: { label: 'Completed', color: STATUS.good },
  processing: { label: 'Processing', color: STATUS.warning },
  failed: { label: 'Failed', color: STATUS.critical },
  deleted: { label: 'Deleted', color: '#898781' },
};

export const fmt = (n) => (n ?? 0).toLocaleString();

export const fmtCompact = (n) => {
  if (n == null) return '';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
};

const LABEL_OVERRIDES = { '(unassigned)': 'Unassigned' };

export const prettyLabel = (key) =>
  LABEL_OVERRIDES[key] ??
  String(key ?? '').replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());

export const relativeTime = (isoStr) => {
  if (!isoStr) return '—';
  const diff = Math.floor((Date.now() - new Date(isoStr + 'Z')) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const statusBadge = (status) => {
  const map = {
    success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    completed: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    partial_error: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
    failed: 'bg-red-50 text-red-700 ring-1 ring-red-100',
  };
  return map[status] ?? 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
};
